import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, RotateCw, Save, X } from "lucide-react";
import ConfirmModal from "@/components/ConfirmModal";

const empty = {
  codice: "", sconto_percentuale: 10, durata: "permanente",
  durata_mesi: 0, data_scadenza: "", max_utilizzi: 0,
  ruolo_assegnato: "standard", attivo: true,
};

const isExpired = (p) => {
  if (!p.data_scadenza) return false;
  try { return new Date(p.data_scadenza) < new Date(); } catch { return false; }
};

export default function AdminPromoCodes() {
  const [promos, setPromos] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = () => api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const normalizePayload = (form) => {
    const payload = { ...form };
    if (!payload.data_scadenza) delete payload.data_scadenza;
    if (!payload.durata_mesi) delete payload.durata_mesi;
    return payload;
  };

  const create = async () => {
    try {
      await api.post("/admin/promo-codes", normalizePayload(createForm));
      toast.success("Codice creato");
      setShowCreate(false); setCreateForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      codice: p.codice,
      sconto_percentuale: p.sconto_percentuale,
      durata: p.durata,
      durata_mesi: p.durata_mesi || 0,
      data_scadenza: p.data_scadenza || "",
      max_utilizzi: p.max_utilizzi || 0,
      ruolo_assegnato: p.ruolo_assegnato,
      attivo: p.attivo,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/promo-codes/${editingId}`, normalizePayload(editForm));
      toast.success("Codice aggiornato");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const renew = async (id) => {
    try {
      await api.post(`/admin/promo-codes/${id}/renew`);
      toast.success("Scadenza estesa di 30 giorni");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/promo-codes/${pendingDelete.id}`);
      setPromos((arr) => arr.filter((p) => p.id !== pendingDelete.id));
      toast.success("Codice eliminato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setPendingDelete(null);
  };

  return (
    <div className="space-y-6 fade-slide">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">Codici promo</h1>
        <button className="vg-btn-primary" onClick={() => setShowCreate(!showCreate)} data-testid="promo-new-btn">
          <Plus size={16} /> Nuovo codice
        </button>
      </div>

      {showCreate && (
        <PromoForm
          title="Nuovo codice"
          form={createForm}
          onChange={setCreateForm}
          onSubmit={create}
          onCancel={() => { setShowCreate(false); setCreateForm(empty); }}
          submitLabel="Crea"
          testidPrefix="promo-form"
        />
      )}

      <div className="vg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left">Codice</th>
              <th className="px-4 py-3">Sconto</th>
              <th className="px-4 py-3">Ruolo</th>
              <th className="px-4 py-3">Utilizzi</th>
              <th className="px-4 py-3">Scadenza</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {promos.map((p) => {
              const expired = isExpired(p);
              const inactive = !p.attivo || expired;
              const rowClass = inactive ? "opacity-50" : "";
              const codeClass = inactive ? "line-through text-gray-400" : "text-[#B40101]";
              return (
                <tr key={p.id} className={`border-t border-gray-100 ${rowClass}`} data-testid={`promo-row-${p.id}`}>
                  <td className={`px-4 py-3 font-mono font-bold ${codeClass}`}>{p.codice}</td>
                  <td className="px-4 py-3 text-center">{p.sconto_percentuale}%</td>
                  <td className="px-4 py-3 text-center">{p.ruolo_assegnato}</td>
                  <td className="px-4 py-3 text-center">{p.utilizzi_correnti}/{p.max_utilizzi || "∞"}</td>
                  <td className="px-4 py-3 text-center text-xs">
                    {p.data_scadenza ? (
                      <span className={expired ? "text-[#B40101] font-semibold" : "text-gray-500"}>
                        {new Date(p.data_scadenza).toLocaleDateString("it-IT")}
                        {expired && " (scaduto)"}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {p.attivo && !expired
                      ? <span className="vg-badge" style={{ background: "#D1FAE5", color: "#065F46" }}>Attivo</span>
                      : <span className="vg-badge" style={{ background: "#E5E7EB", color: "#6B7280" }}>{expired ? "Scaduto" : "Inattivo"}</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button onClick={() => renew(p.id)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-gray-200 hover:bg-gray-50 text-[#0A0A0A]" title="Estendi scadenza di 30 giorni" data-testid={`promo-renew-${p.id}`}>
                        <RotateCw size={14} /> Rinnova
                      </button>
                      <button onClick={() => startEdit(p)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-gray-200 hover:bg-gray-50 text-[#0A0A0A]" data-testid={`promo-edit-${p.id}`}>
                        <Pencil size={14} /> Modifica
                      </button>
                      <button onClick={() => setPendingDelete(p)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-[10px] border border-red-200 text-[#B40101] hover:bg-red-50" data-testid={`promo-delete-${p.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editingId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingId(null)}>
          <div className="bg-white rounded-[12px] w-full max-w-2xl p-2 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <PromoForm
              title="Modifica codice"
              form={editForm}
              onChange={setEditForm}
              onSubmit={saveEdit}
              onCancel={() => setEditingId(null)}
              submitLabel="Salva modifiche"
              testidPrefix="promo-edit-form"
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!pendingDelete}
        title="Elimina codice promo"
        message={`Sei sicuro di voler eliminare il codice "${pendingDelete?.codice}"? L'operazione non è reversibile.`}
        confirmLabel="Conferma elimina"
        destructive
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testid="promo-delete-modal"
      />
    </div>
  );
}

function PromoForm({ title, form, onChange, onSubmit, onCancel, submitLabel, testidPrefix }) {
  const upd = (patch) => onChange({ ...form, ...patch });
  return (
    <div className="vg-card p-6 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: "Outfit" }}>{title}</h3>
        <button onClick={onCancel} data-testid={`${testidPrefix}-close`}><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Codice">
          <input className="vg-input" value={form.codice} onChange={(e) => upd({ codice: e.target.value })} data-testid={`${testidPrefix}-codice`} />
        </Field>
        <Field label="Sconto %">
          <input type="number" min="0" max="100" className="vg-input" value={form.sconto_percentuale} onChange={(e) => upd({ sconto_percentuale: Number(e.target.value) })} data-testid={`${testidPrefix}-sconto`} />
        </Field>
        <Field label="Ruolo assegnato">
          <select className="vg-input" value={form.ruolo_assegnato} onChange={(e) => upd({ ruolo_assegnato: e.target.value })} data-testid={`${testidPrefix}-ruolo`}>
            <option value="standard">standard</option>
            <option value="fpa">fpa</option>
          </select>
        </Field>
        <Field label="Max utilizzi (0 = illimitato)">
          <input type="number" className="vg-input" value={form.max_utilizzi} onChange={(e) => upd({ max_utilizzi: Number(e.target.value) })} data-testid={`${testidPrefix}-max`} />
        </Field>
        <Field label="Durata">
          <select className="vg-input" value={form.durata} onChange={(e) => upd({ durata: e.target.value })} data-testid={`${testidPrefix}-durata`}>
            <option value="permanente">permanente</option>
            <option value="mesi">mesi</option>
          </select>
        </Field>
        {form.durata === "mesi" && (
          <Field label="Mesi durata">
            <input type="number" className="vg-input" value={form.durata_mesi} onChange={(e) => upd({ durata_mesi: Number(e.target.value) })} data-testid={`${testidPrefix}-durata-mesi`} />
          </Field>
        )}
        <Field label="Scadenza (opzionale)">
          <input type="date" className="vg-input" value={form.data_scadenza?.slice(0,10) || ""} onChange={(e) => upd({ data_scadenza: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid={`${testidPrefix}-scadenza`} />
        </Field>
        <Field label="Stato">
          <select className="vg-input" value={form.attivo ? "true" : "false"} onChange={(e) => upd({ attivo: e.target.value === "true" })} data-testid={`${testidPrefix}-attivo`}>
            <option value="true">Attivo</option>
            <option value="false">Inattivo</option>
          </select>
        </Field>
      </div>
      <div className="flex gap-2 justify-end mt-3">
        <button className="vg-btn-secondary" onClick={onCancel} data-testid={`${testidPrefix}-cancel`}>Annulla</button>
        <button className="vg-btn-primary" onClick={onSubmit} data-testid={`${testidPrefix}-submit`}>
          <Save size={14} /> {submitLabel}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
